/**
 * Shared hooks for Services & Workflows configuration.
 *
 * Consolidates data fetching and transformation logic for both
 * Firm Services and Matter Services contexts.
 */
import { useMemo } from 'react';

import {
  enhancedApi,
  type FirmServiceWorkflowSchema,
  type ServiceCatalogueItemSchema,
  type WorkflowCatalogueItemSchema,
} from '@/services/api/generated/firmsService';
import {
  type MatterServiceWithWorkflowsResponse,
  useCreateMatterServiceMutation,
  useGetMatterServicesQuery,
} from '@/services/api/mattersService';

import { SERVICE_DISPLAY_ORDER } from './constants';
import type {
  DisplayServiceWithWorkflows,
  DisplayWorkflow,
  FirmDisplayService,
  MatterDisplayService,
} from './types';

// -----------------------------------------------------------------------------
// Shared Utilities
// -----------------------------------------------------------------------------

/**
 * Sort services by the predefined display order.
 * Services not in the order list are sorted alphabetically at the end.
 */
export const sortServicesByDisplayOrder = <
  T extends DisplayServiceWithWorkflows,
>(
  services: T[],
): T[] => {
  return [...services].sort((a, b) => {
    const indexA = SERVICE_DISPLAY_ORDER.indexOf(
      a.service_name as (typeof SERVICE_DISPLAY_ORDER)[number],
    );
    const indexB = SERVICE_DISPLAY_ORDER.indexOf(
      b.service_name as (typeof SERVICE_DISPLAY_ORDER)[number],
    );

    if (indexA === -1 && indexB === -1) {
      const nameA = a.display_name || a.service_name || '';
      const nameB = b.display_name || b.service_name || '';
      return nameA.localeCompare(nameB);
    }
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

/**
 * Transform a catalogue workflow to a display workflow.
 * Used when no firm/matter configuration exists.
 */
const catalogueWorkflowToDisplay = (
  w: WorkflowCatalogueItemSchema,
): DisplayWorkflow => ({
  workflow_code: w.code,
  display_name: w.display_name,
  description: w.description ?? null,
  status: w.status ?? 'active',
  is_enabled: false,
  notes: '',
  task_types: w.task_types ?? [],
});

/**
 * Transform a firm workflow schema to a display workflow.
 */
const firmWorkflowToDisplay = (
  w: FirmServiceWorkflowSchema,
): DisplayWorkflow => ({
  workflow_code: w.workflow_code,
  display_name: w.display_name,
  description: w.description,
  status: w.status,
  is_enabled: w.is_enabled,
  notes: w.notes || '',
  task_types: w.task_types,
});

// -----------------------------------------------------------------------------
// Firm Services Hook
// -----------------------------------------------------------------------------

interface UseFirmServicesWithCatalogueResult {
  /** Merged and sorted services ready for display */
  services: FirmDisplayService[];
  /** Loading state for either query */
  isLoading: boolean;
  /** Create a FirmService mutation (for auto-creating on first click) */
  createFirmService: ReturnType<
    typeof enhancedApi.useCreateFirmServiceMutation
  >[0];
  /** Whether creation is in progress */
  isCreating: boolean;
}

/**
 * Fetches the service catalogue and firm configuration, merges them for display.
 *
 * Uses the overlay pattern:
 * - Global catalogue defines all available services/workflows
 * - Firm configuration overlays enabled states and notes
 * - UI always shows complete picture by merging both
 */
export const useFirmServicesWithCatalogue = (
  firmId: string,
): UseFirmServicesWithCatalogueResult => {
  const { data: catalogue, isLoading: isLoadingCatalogue } =
    enhancedApi.useGetServiceCatalogueQuery();

  const { data: firmServices, isLoading: isLoadingFirm } =
    enhancedApi.useGetFirmServicesQuery({ firmId });

  const [createFirmService, { isLoading: isCreating }] =
    enhancedApi.useCreateFirmServiceMutation();

  const isLoading = isLoadingCatalogue || isLoadingFirm;

  const services = useMemo((): FirmDisplayService[] => {
    if (!catalogue?.services) return [];

    // Build lookup from service_id to firm service data
    const firmServiceMap = new Map(
      firmServices?.map((fs) => [fs.service_id, fs]) ?? [],
    );

    const transformed = catalogue.services.map(
      (service: ServiceCatalogueItemSchema): FirmDisplayService => {
        const firmService = firmServiceMap.get(service.id);
        // Cast workflows to the correct type (backend returns FirmServiceWorkflowSchema[])
        const firmWorkflows = (firmService?.workflows ??
          []) as FirmServiceWorkflowSchema[];

        return {
          id: firmService?.id ?? service.id,
          service_id: service.id,
          service_name: service.name,
          display_name: service.display_name,
          service: {
            id: service.id,
            name: service.name,
            display_name: service.display_name,
            description: service.description,
          },
          is_active: firmService?.is_active ?? false,
          isFirmService: !!firmService,
          firmWorkflows,
          workflows:
            firmWorkflows.length > 0
              ? firmWorkflows.map(firmWorkflowToDisplay)
              : (service.workflows ?? []).map(catalogueWorkflowToDisplay),
        };
      },
    );

    return sortServicesByDisplayOrder(transformed);
  }, [catalogue, firmServices]);

  return {
    services,
    isLoading,
    createFirmService,
    isCreating,
  };
};

// -----------------------------------------------------------------------------
// Matter Services Hook
// -----------------------------------------------------------------------------

interface UseMatterServicesWithCatalogueResult {
  /** Services with workflows ready for display (all from catalogue) */
  services: MatterDisplayService[];
  /** Map from service_id to matter_service_id for API calls */
  serviceIdToMatterServiceId: Map<string, string>;
  /** Loading state */
  isLoading: boolean;
  /** Whether the matter has any configured services */
  hasMatterServices: boolean;
  /** Error state */
  error: Error | null;
  /** Create a MatterService mutation (for auto-creating on toggle) */
  createMatterService: ReturnType<typeof useCreateMatterServiceMutation>[0];
  /** Whether creation is in progress */
  isCreating: boolean;
}

/**
 * Fetches the service catalogue and matter configuration, merges them for display.
 *
 * Architecture matches useFirmServicesWithCatalogue:
 * - Fetches global service catalogue (all available services)
 * - Fetches matter's configured services
 * - Merges them so ALL services appear, with matter config applied where it exists
 * - Exposes createMatterService for auto-creating on toggle
 */
export const useMatterServicesWithCatalogue = (
  matterId: string | null,
  firmId: string | null,
): UseMatterServicesWithCatalogueResult => {
  const {
    data: catalogue,
    isLoading: isLoadingCatalogue,
    error: catalogueError,
  } = enhancedApi.useGetServiceCatalogueQuery(undefined, {
    skip: !matterId || !firmId,
  });

  const {
    data: matterServicesData,
    isLoading: isLoadingMatterServices,
    error: matterError,
  } = useGetMatterServicesQuery(
    { matterId: matterId ?? '' },
    { skip: !matterId },
  );

  const [createMatterService, { isLoading: isCreating }] =
    useCreateMatterServiceMutation();

  const isLoading = isLoadingCatalogue || isLoadingMatterServices;

  const error = useMemo(() => {
    if (catalogueError) return new Error('Failed to load service catalogue');
    if (matterError) return new Error('Failed to load matter services');
    return null;
  }, [catalogueError, matterError]);

  const hasMatterServices = useMemo(() => {
    return (matterServicesData?.length ?? 0) > 0;
  }, [matterServicesData]);

  const serviceIdToMatterServiceId = useMemo(() => {
    if (!matterServicesData) return new Map<string, string>();
    return new Map(matterServicesData.map((s) => [s.service_id, s.id]));
  }, [matterServicesData]);

  const services = useMemo((): MatterDisplayService[] => {
    if (!catalogue?.services) return [];

    const matterServiceMap = new Map<
      string,
      MatterServiceWithWorkflowsResponse
    >(matterServicesData?.map((ms) => [ms.service_id, ms]) ?? []);

    const transformed = catalogue.services.map(
      (service: ServiceCatalogueItemSchema): MatterDisplayService => {
        const matterService = matterServiceMap.get(service.id);

        if (matterService) {
          return {
            service_id: service.id,
            service_name: service.name,
            display_name: service.display_name,
            is_active: matterService.is_active,
            isMatterService: true,
            workflows: matterService.workflows.map(
              (w): DisplayWorkflow => ({
                workflow_code: w.workflow_code,
                display_name: w.display_name,
                description: w.description,
                status: w.status,
                is_enabled: w.is_enabled,
                notes: w.notes || '',
                task_types: w.task_types,
              }),
            ),
          };
        }

        // Matter doesn't have this service - show catalogue defaults (all disabled)
        return {
          service_id: service.id,
          service_name: service.name,
          display_name: service.display_name,
          is_active: false,
          isMatterService: false,
          workflows: (service.workflows ?? []).map(catalogueWorkflowToDisplay),
        };
      },
    );

    return sortServicesByDisplayOrder(transformed);
  }, [catalogue, matterServicesData]);

  return {
    services,
    serviceIdToMatterServiceId,
    isLoading,
    hasMatterServices,
    error,
    createMatterService,
    isCreating,
  };
};

// -----------------------------------------------------------------------------
// Firm Services for Form (read-only, used in create matter flow)
// -----------------------------------------------------------------------------

interface UseFirmServicesForFormResult {
  /** Services with workflows ready for display */
  services: DisplayServiceWithWorkflows[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

/**
 * Fetches firm services configuration for use in forms (read-only).
 * Used by CREATE matter flow to get firm default settings.
 */
export const useFirmServicesForForm = (
  firmId: string | null,
): UseFirmServicesForFormResult => {
  const {
    data: catalogue,
    isLoading: isLoadingCatalogue,
    error: catalogueError,
  } = enhancedApi.useGetServiceCatalogueQuery(undefined, {
    skip: !firmId,
  });

  const {
    data: firmServices,
    isLoading: isLoadingFirm,
    error: firmError,
  } = enhancedApi.useGetFirmServicesQuery(
    { firmId: firmId ?? '' },
    { skip: !firmId },
  );

  const isLoading = isLoadingCatalogue || isLoadingFirm;

  const error = useMemo(() => {
    if (catalogueError) return new Error('Failed to load service catalogue');
    if (firmError) return new Error('Failed to load firm services');
    return null;
  }, [catalogueError, firmError]);

  const services = useMemo((): DisplayServiceWithWorkflows[] => {
    if (!firmId || !catalogue?.services) return [];

    // Build lookup for firm service configuration
    const firmServiceMap = new Map(
      firmServices?.map((fs) => [fs.service_id, fs]) ?? [],
    );

    const transformed = catalogue.services.map(
      (service: ServiceCatalogueItemSchema): DisplayServiceWithWorkflows => {
        const firmService = firmServiceMap.get(service.id);
        const firmWorkflows = (firmService?.workflows ??
          []) as FirmServiceWorkflowSchema[];

        // Build lookup for firm workflow enabled states
        const firmWorkflowMap = new Map<string, boolean>(
          firmWorkflows.map((w) => [w.workflow_code, w.is_enabled]),
        );

        // Transform workflows with firm defaults
        const workflows: DisplayWorkflow[] = (service.workflows ?? []).map(
          (w: WorkflowCatalogueItemSchema): DisplayWorkflow => ({
            workflow_code: w.code,
            display_name: w.display_name,
            description: w.description ?? null,
            status: w.status ?? 'active',
            is_enabled: firmWorkflowMap.get(w.code) ?? false,
            notes: '', // Notes are case-specific, start empty
            task_types: [],
          }),
        );

        return {
          service_id: service.id,
          service_name: service.name,
          display_name: service.display_name,
          is_active: firmService?.is_active ?? false,
          workflows,
        };
      },
    );

    return sortServicesByDisplayOrder(transformed);
  }, [firmId, catalogue, firmServices]);

  return {
    services,
    isLoading,
    error,
  };
};
